'use client';

import * as React from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X as XIcon, Loader2, Trash2 } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { cn } from '@/shared/lib/cn';
import { Button } from '@/shared/ui/shadcn/button';
import { sheetOverlayTint } from '@/shared/ui/shadcn/modal-chrome';

interface AdaptiveDialogProps {
  open: boolean;
  onClose: () => void;
  title: string;
  onSave: () => void;
  saveEnabled?: boolean;
  isSaving?: boolean;
  saveLabel?: string;
  /**
   * Optional icon-only confirm action for the mobile top bar — used where a
   * text label would mislead (e.g. the concept "Start design" sheet confirms
   * with a checkmark, not a "Create design" word; mirrors Apple's iOS 26 HIG
   * icon toolbar). The desktop footer keeps the text label. `saveLabel` still
   * drives the button's accessible name.
   */
  saveIcon?: React.ReactNode;
  /**
   * Render the confirm action in the accent (teal) color to emphasise it as the
   * primary CTA. Only affects the mobile `groupedSurface` chrome, where the
   * confirm button is otherwise a neutral foreground-colored capsule.
   */
  saveAccent?: boolean;
  cancelLabel?: string;
  onDelete?: () => void;
  deleteEnabled?: boolean;
  isDeleting?: boolean;
  deleteLabel?: string;
  children: React.ReactNode;
  contentClassName?: string;
  /**
   * iOS "grouped" surface: the sheet itself is a muted gray backdrop and the
   * content is expected to render its own white cards on top (mirrors Apple's
   * `.formStyle(.grouped)` / systemGroupedBackground). Also restyles the mobile
   * chrome to Apple's create-sheet look — a white circular close button and a
   * white capsule confirm button. Default keeps the plain white surface.
   */
  groupedSurface?: boolean;
}

/**
 * Adaptive dialog that mirrors the Apple platform conventions:
 * - < sm (640px, mobile-like viewport): full-screen sheet with a top bar —
 *   `X` (close) on the left, centered title, `Save` on the right.
 *   Destructive action (when provided) renders as a full-width inline button
 *   at the bottom of the scrollable content (iOS-style).
 * - >= sm (tablet / desktop): centered basic dialog with title at the top,
 *   scrollable content, and a footer with the destructive action on the
 *   bottom-left and Cancel / Save on the bottom-right (macOS-style).
 */
export function AdaptiveDialog({
  open,
  onClose,
  title,
  onSave,
  saveEnabled = true,
  isSaving = false,
  saveLabel,
  saveIcon,
  saveAccent = false,
  cancelLabel,
  onDelete,
  deleteEnabled = true,
  isDeleting = false,
  deleteLabel,
  children,
  contentClassName,
  groupedSurface = false,
}: AdaptiveDialogProps) {
  const tCommon = useTranslations('common');
  const save = saveLabel ?? tCommon('save');
  const cancel = cancelLabel ?? tCommon('cancel');
  const del = deleteLabel ?? tCommon('delete');

  const saveDisabled = !saveEnabled || isSaving || isDeleting;
  const deleteDisabled = !deleteEnabled || isDeleting || isSaving;

  return (
    <DialogPrimitive.Root
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) onClose();
      }}
    >
      <DialogPrimitive.Portal>
        {/* Overlay — soft gray dim (no blur), mirrors Apple sheet backdrop. */}
        <DialogPrimitive.Overlay
          className={cn(
            cn('fixed inset-0 z-50', sheetOverlayTint),
            'transition-opacity duration-200 ease-in-out',
            'data-[state=open]:opacity-100 data-[state=closed]:opacity-0'
          )}
        />
        <DialogPrimitive.Content
          aria-describedby={undefined}
          className={cn(
            'fixed z-50 flex flex-col outline-none',
            groupedSurface ? 'bg-muted' : 'bg-background',
            // Mobile: full-screen sheet
            'inset-0 w-full h-full rounded-none',
            // Tablet / desktop: centered card with soft, borderless Apple-like surface
            'sm:inset-auto sm:top-[50%] sm:left-[50%]',
            'sm:-translate-x-1/2 sm:-translate-y-1/2',
            'sm:w-full sm:max-w-lg sm:max-h-[90vh] sm:h-auto',
            'sm:rounded-[28px] sm:shadow-[0_40px_240px_-20px_rgba(0,0,0,0.22),0_8px_32px_-12px_rgba(0,0,0,0.08)]',
            // Transitions
            'transition-all duration-200 ease-in-out',
            'data-[state=open]:opacity-100 data-[state=open]:scale-100',
            'data-[state=closed]:opacity-0 sm:data-[state=closed]:scale-95',
            contentClassName
          )}
        >
          {/* Mobile top bar — no header divider (mirrors Apple sheets). */}
          <div className="flex sm:hidden items-center justify-between gap-2 px-2 py-2">
            <button
              type="button"
              onClick={onClose}
              className={cn(
                'inline-flex items-center justify-center w-10 h-10',
                groupedSurface
                  ? 'rounded-full bg-background shadow-sm'
                  : 'rounded-md hover:bg-accent'
              )}
              aria-label={tCommon('close')}
            >
              <XIcon className="w-5 h-5" />
            </button>
            <DialogPrimitive.Title className="flex-1 text-center text-list-item-title truncate">
              {title}
            </DialogPrimitive.Title>
            <Button
              variant="ghost"
              onClick={onSave}
              disabled={saveDisabled}
              aria-label={saveIcon ? save : undefined}
              className={cn(
                groupedSurface
                  ? cn(
                      'rounded-full bg-background font-semibold shadow-sm px-4 hover:bg-background',
                      saveAccent ? 'text-primary' : 'text-foreground'
                    )
                  : 'text-primary font-medium'
              )}
            >
              {isSaving ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                saveIcon ?? save
              )}
            </Button>
          </div>

          {/* Desktop title */}
          <div className="hidden sm:block px-7 pt-7 pb-5">
            <DialogPrimitive.Title className="text-sheet-title">
              {title}
            </DialogPrimitive.Title>
          </div>

          {/* Scrollable content. Mobile keeps `flex-1` to fill the sheet,
              desktop uses `sm:flex-initial` so the dialog hugs its content
              (no empty whitespace below short inputs) yet can still SHRINK and
              scroll when content exceeds `max-h`. `flex-none` would pin the
              shrink to 0 — content then overflows the 90vh shell, pushes the
              footer off-screen and the inner `overflow-y-auto` never engages.
              `min-h-0` lets the flex item shrink below its content height. */}
          <div className="flex-1 sm:flex-initial min-h-0 overflow-y-auto px-4 sm:px-7 pb-5">
            {children}

            {/* Mobile-only inline destructive action */}
            {onDelete && (
              <div className="sm:hidden pt-4">
                <Button
                  variant="ghost"
                  onClick={onDelete}
                  disabled={deleteDisabled}
                  className="w-full text-destructive hover:text-destructive"
                >
                  {isDeleting ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      <Trash2 className="w-4 h-4 me-2" />
                      {del}
                    </>
                  )}
                </Button>
              </div>
            )}
          </div>

          {/* Desktop footer */}
          <div className="hidden sm:flex items-center gap-2 border-t px-7 py-5">
            {onDelete ? (
              <Button
                variant="ghost"
                onClick={onDelete}
                disabled={deleteDisabled}
                className="text-destructive hover:text-destructive"
              >
                {isDeleting ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    <Trash2 className="w-4 h-4 me-2" />
                    {del}
                  </>
                )}
              </Button>
            ) : null}
            <div className="ms-auto flex gap-2">
              <Button variant="secondary" onClick={onClose} disabled={isSaving}>
                {cancel}
              </Button>
              <Button onClick={onSave} disabled={saveDisabled}>
                {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : save}
              </Button>
            </div>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
